import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@r/utils/utils.ts';

/**
 * Frise chronologique verticale, composable façon shadcn.
 *
 * @example
 * <Timeline>
 *   <TimelineItem>
 *     <TimelineOppositeContent className="w-24">12 mars</TimelineOppositeContent>
 *     <TimelineSeparator>
 *       <TimelineDot variant="active" />
 *       <TimelineConnector />
 *     </TimelineSeparator>
 *     <TimelineContent>…</TimelineContent>
 *   </TimelineItem>
 * </Timeline>
 */
const Timeline = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col', className)} {...props} />
  )
);
Timeline.displayName = 'Timeline';

const TimelineItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex gap-4', className)} {...props} />
  )
);
TimelineItem.displayName = 'TimelineItem';

/** Colonne de gauche (date, libellé…). Largeur à fixer par le consommateur via `className`. */
const TimelineOppositeContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('shrink-0 pt-0.5 text-right text-sm', className)} {...props} />
  )
);
TimelineOppositeContent.displayName = 'TimelineOppositeContent';

/** Rail central : contient le point puis le connecteur. */
const TimelineSeparator = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col items-center', className)} {...props} />
  )
);
TimelineSeparator.displayName = 'TimelineSeparator';

const timelineDotVariants = cva('mt-0.5 box-border shrink-0 rounded-full border-2', {
  variants: {
    variant: {
      default: 'border-muted-foreground/40 bg-background',
      active: 'border-primary bg-primary',
      outline: 'border-border bg-background'
    },
    size: {
      sm: 'size-2',
      default: 'size-3',
      lg: 'size-4'
    }
  },
  defaultVariants: { variant: 'default', size: 'default' }
});

interface TimelineDotProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof timelineDotVariants> {}

const TimelineDot = forwardRef<HTMLSpanElement, TimelineDotProps>(
  ({ className, variant, size, ...props }, ref) => (
    <span ref={ref} className={cn(timelineDotVariants({ variant, size }), className)} {...props} />
  )
);
TimelineDot.displayName = 'TimelineDot';

/** Trait vertical reliant deux points. À omettre sur le dernier item. */
const TimelineConnector = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('w-px flex-1 bg-border', className)} {...props} />
  )
);
TimelineConnector.displayName = 'TimelineConnector';

/** Contenu de droite (carte, texte…). */
const TimelineContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex-1 pb-6', className)} {...props} />
  )
);
TimelineContent.displayName = 'TimelineContent';

export {
  Timeline,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  timelineDotVariants
};
